import { ArrowLeft } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAthleteThresholds } from '../../hooks/useAthleteThresholds'
import type { AthleteThreshold } from '../../types/database.tables'
import type { ZoneModel } from '../../types/zones'
import { DEFAULT_HR_ZONES, DEFAULT_PACE_ZONES, DEFAULT_POWER_ZONES } from '../../types/zones'
import { calculateHRZones, calculatePaceZones, calculatePowerZones } from '../../utils/zoneCalculations'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'
import { ZonePreview } from '../zones/ZonePreview'

const Container = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.xxl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  flex: 1;
`

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const Form = styled.form`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  height: fit-content;
`

const ZonePreviewContainer = styled.div`
  position: sticky;
  top: ${({ theme }) => theme.spacing.lg};
  height: fit-content;
  max-height: calc(100vh - 200px);

  @media (max-width: 1024px) {
    position: static;
    max-height: 600px;
  }
`

const FormRow = styled.div`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const FormGroup = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.md};
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
`

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.error}20;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

interface ThresholdFormData {
  activity_type: string
  vt1_bpm: string
  vt1_pace?: string
  vt1_power?: string
  vt2_bpm: string
  vt2_pace?: string
  vt2_power?: string
  max_hr_bpm: string
  vo2_max?: string
  w_prime_kj?: string
  critical_power_watts?: string
  effective_date: string
  source: string
  notes: string
  hr_zone_config?: ZoneModel
  pace_zone_config?: ZoneModel
  power_zone_config?: ZoneModel
}

const emptyFormData: ThresholdFormData = {
  activity_type: 'running',
  vt1_bpm: '',
  vt2_bpm: '',
  max_hr_bpm: '',
  effective_date: new Date().toISOString().split('T')[0],
  source: 'lab_test',
  notes: '',
}

// Helper function to parse pace strings
const parsePace = (pace: string): number | null => {
  const parts = pace.split(':')
  if (parts.length !== 2) {
    return null
  }
  const minutes = Number.parseInt(parts[0], 10)
  const seconds = Number.parseInt(parts[1], 10)
  if (Number.isNaN(minutes) || Number.isNaN(seconds) || minutes < 0 || seconds < 0 || minutes > 59 || seconds > 59) {
    return null
  }
  return minutes * 60 + seconds
}

const formatPace = (pace: number | null): string | null => {
  if (pace === null) {
    return null
  }
  const minutes = Math.floor(pace / 60)
  const seconds = pace % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export const EditThresholdView: React.FC = () => {
  const { id: athleteId, thresholdId } = useParams<{ id: string; thresholdId: string }>()
  const navigate = useNavigate()
  const { thresholds, loading, addThreshold, updateThreshold, deleteThreshold } = useAthleteThresholds(athleteId || '')

  const [formData, setFormData] = useState<ThresholdFormData>(emptyFormData)
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Zone models state
  const [hrZoneModel, setHrZoneModel] = useState<ZoneModel>(DEFAULT_HR_ZONES)
  const [paceZoneModel, setPaceZoneModel] = useState<ZoneModel>(DEFAULT_PACE_ZONES)
  const [powerZoneModel, setPowerZoneModel] = useState<ZoneModel>(DEFAULT_POWER_ZONES)

  const isEditing = thresholdId !== 'new'

  // Load threshold data if editing
  useEffect(() => {
    if (isEditing && !loading && thresholds.length > 0) {
      const threshold = thresholds.find((t) => t.id === thresholdId)
      if (threshold) {
        setFormData({
          activity_type: threshold.activity_type || 'running',
          vt1_bpm: threshold.vt1_bpm?.toString() || '',
          vt1_pace: formatPace(threshold.vt1_pace_min_per_kmh) || '',
          vt1_power: threshold.vt1_power_watts?.toString() || '',
          vt2_bpm: threshold.vt2_bpm?.toString() || '',
          vt2_pace: formatPace(threshold.vt2_pace_min_per_kmh) || '',
          vt2_power: threshold.vt2_power_watts?.toString() || '',
          max_hr_bpm: threshold.max_hr_bpm?.toString() || '',
          effective_date: threshold.effective_date || new Date().toISOString().split('T')[0],
          source: threshold.source || 'manual_entry',
          notes: threshold.notes || '',
        })

        // Load zone configs from threshold or use defaults
        setHrZoneModel(DEFAULT_HR_ZONES)
        setPaceZoneModel(DEFAULT_PACE_ZONES)
        setPowerZoneModel(DEFAULT_POWER_ZONES)

        // TODO: load custom zones from database
      }
    }
  }, [isEditing, loading, thresholds, thresholdId])

  // Calculate zones based on current form values
  const calculatedHRZones = useMemo(() => {
    return calculateHRZones(hrZoneModel, {
      vt1Bpm: formData.vt1_bpm ? Number.parseInt(formData.vt1_bpm, 10) : null,
      vt2Bpm: formData.vt2_bpm ? Number.parseInt(formData.vt2_bpm, 10) : null,
      maxHrBpm: formData.max_hr_bpm ? Number.parseInt(formData.max_hr_bpm, 10) : null,
    })
  }, [hrZoneModel, formData.vt1_bpm, formData.vt2_bpm, formData.max_hr_bpm])

  const calculatedPaceZones = useMemo(() => {
    return calculatePaceZones(paceZoneModel, {
      vt1PaceSeconds: formData.vt1_pace ? parsePace(formData.vt1_pace) : null,
      vt2PaceSeconds: formData.vt2_pace ? parsePace(formData.vt2_pace) : null,
    })
  }, [paceZoneModel, formData.vt1_pace, formData.vt2_pace])

  const calculatedPowerZones = useMemo(() => {
    return calculatePowerZones(powerZoneModel, {
      vt1PowerWatts: formData.vt1_power ? Number.parseInt(formData.vt1_power, 10) : null,
      vt2PowerWatts: formData.vt2_power ? Number.parseInt(formData.vt2_power, 10) : null,
    })
  }, [powerZoneModel, formData.vt1_power, formData.vt2_power])

  const calculatedZones = useMemo(() => {
    return {
      hr: calculatedHRZones,
      pace: calculatedPaceZones,
      power: calculatedPowerZones,
    }
  }, [calculatedHRZones, calculatedPaceZones, calculatedPowerZones])

  const thresholdValues = useMemo(() => {
    return {
      vt1Bpm: formData.vt1_bpm ? Number.parseInt(formData.vt1_bpm, 10) : null,
      vt2Bpm: formData.vt2_bpm ? Number.parseInt(formData.vt2_bpm, 10) : null,
      maxHrBpm: formData.max_hr_bpm ? Number.parseInt(formData.max_hr_bpm, 10) : null,
      vt1PaceSeconds: formData.vt1_pace ? parsePace(formData.vt1_pace) : null,
      vt2PaceSeconds: formData.vt2_pace ? parsePace(formData.vt2_pace) : null,
      vt1PowerWatts: formData.vt1_power ? Number.parseInt(formData.vt1_power, 10) : null,
      vt2PowerWatts: formData.vt2_power ? Number.parseInt(formData.vt2_power, 10) : null,
    }
  }, [formData])

  const handleZoneModelChange = (type: 'heart_rate' | 'pace' | 'power', zoneModel: ZoneModel) => {
    switch (type) {
      case 'heart_rate':
        setHrZoneModel(zoneModel)
        break
      case 'pace':
        setPaceZoneModel(zoneModel)
        break
      case 'power':
        setPowerZoneModel(zoneModel)
        break
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setFormError(null)
  }

  const handleBack = () => {
    navigate(`/athletes/${athleteId}/thresholds`)
  }

  const handleDelete = async () => {
    if (!isEditing || !thresholdId) return

    if (window.confirm('Are you sure you want to delete this threshold?')) {
      await deleteThreshold(thresholdId)
      handleBack()
    }
  }

  const validateForm = (): string | null => {
    // At least one threshold value must be provided
    if (!formData.vt1_bpm && !formData.vt2_bpm && !formData.max_hr_bpm) {
      return 'At least one threshold value (VT1, VT2, or Max HR) must be provided'
    }

    // Validate numeric values
    const vt1 = formData.vt1_bpm ? Number.parseInt(formData.vt1_bpm) : null
    const vt2 = formData.vt2_bpm ? Number.parseInt(formData.vt2_bpm) : null
    const maxHr = formData.max_hr_bpm ? Number.parseInt(formData.max_hr_bpm) : null

    if (vt1 !== null && (Number.isNaN(vt1) || vt1 <= 0 || vt1 > 220)) {
      return 'VT1 must be between 1 and 220 bpm'
    }

    if (vt2 !== null && (Number.isNaN(vt2) || vt2 <= 0 || vt2 > 220)) {
      return 'VT2 must be between 1 and 220 bpm'
    }

    if (maxHr !== null && (Number.isNaN(maxHr) || maxHr <= 0 || maxHr > 220)) {
      return 'Max HR must be between 1 and 220 bpm'
    }

    // VT1 must be less than VT2 if both are provided
    if (vt1 !== null && vt2 !== null && vt1 >= vt2) {
      return 'VT1 must be less than VT2'
    }

    // Effective date is required
    if (!formData.effective_date) {
      return 'Effective date is required'
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setFormError(validationError)
      return
    }

    setIsSubmitting(true)
    setFormError(null)

    try {
      const thresholdData: Omit<AthleteThreshold, 'id' | 'created_at' | 'updated_at'> = {
        athlete_id: athleteId || '',
        activity_type: formData.activity_type ?? '',
        vt1_bpm: formData.vt1_bpm ? Number.parseInt(formData.vt1_bpm, 10) : null,
        vt1_pace_min_per_kmh: formData.vt1_pace ? parsePace(formData.vt1_pace) : null,
        vt1_power_watts: formData.vt1_power ? Number.parseInt(formData.vt1_power, 10) : null,
        vt2_bpm: formData.vt2_bpm ? Number.parseInt(formData.vt2_bpm, 10) : null,
        vt2_pace_min_per_kmh: formData.vt2_pace ? parsePace(formData.vt2_pace) : null,
        vt2_power_watts: formData.vt2_power ? Number.parseInt(formData.vt2_power, 10) : null,
        max_hr_bpm: formData.max_hr_bpm ? Number.parseInt(formData.max_hr_bpm, 10) : null,
        effective_date: formData.effective_date,
        source: formData.source || null,
        notes: formData.notes || null,
        hr_zone_config: calculatedHRZones as any,
        pace_zone_config: calculatedPaceZones as any,
        power_zone_config: calculatedPowerZones as any,
        critical_power_watts: formData.critical_power_watts ? Number.parseInt(formData.critical_power_watts, 10) : null,
        vo2max: formData.vo2_max ? Number.parseInt(formData.vo2_max, 10) : null,
        w_prime_kj: formData.w_prime_kj ? Number.parseFloat(formData.w_prime_kj) : null,
      }

      if (isEditing && thresholdId) {
        await updateThreshold(thresholdId, thresholdData)
      } else {
        await addThreshold(thresholdData)
      }

      // Navigate back to thresholds list
      handleBack()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save threshold')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!athleteId) {
    return <div>Invalid athlete ID</div>
  }

  return (
    <Container>
      <Header>
        <ActionButton variant="default" onClick={handleBack}>
          <ArrowLeft size={16} />
        </ActionButton>
        <Title>{isEditing ? 'Edit Threshold' : 'Add New Threshold'}</Title>
      </Header>

      <Content>
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            {formError && <ErrorMessage>{formError}</ErrorMessage>}

            <FormRow>
              <FormGroup>
                <Label htmlFor="activity_type">Activity Type *</Label>
                <Select
                  id="activity_type"
                  name="activity_type"
                  value={formData.activity_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="running">Running</option>
                  <option value="cycling">Cycling</option>
                  <option value="swimming">Swimming</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="effective_date">Effective Date *</Label>
                <Input
                  type="date"
                  id="effective_date"
                  name="effective_date"
                  value={formData.effective_date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="source">Source</Label>
                <Select id="source" name="source" value={formData.source} onChange={handleInputChange}>
                  <option value="lab_test">Lab Test</option>
                  <option value="field_test">Field Test</option>
                  <option value="estimated">Estimated</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup style={{ maxWidth: '300px' }}>
                <Label htmlFor="vt1_bpm">VT1 HR (bpm)</Label>
                <Input
                  type="number"
                  id="vt1_bpm"
                  name="vt1_bpm"
                  value={formData.vt1_bpm}
                  onChange={handleInputChange}
                  placeholder="e.g., 145"
                  min="1"
                  max="220"
                />
              </FormGroup>

              {formData.activity_type === 'running' && (
                <FormGroup style={{ maxWidth: '300px' }}>
                  <Label htmlFor="vt1_pace">VT1 Pace (mm:ss/km)</Label>
                  <Input
                    type="string"
                    id="vt1_pace"
                    name="vt1_pace"
                    value={formData.vt1_pace}
                    onChange={handleInputChange}
                    placeholder="e.g., 4:20"
                  />
                </FormGroup>
              )}

              {formData.activity_type === 'cycling' && (
                <FormGroup style={{ maxWidth: '300px' }}>
                  <Label htmlFor="vt2_bpm">VT1 Power (W)</Label>
                  <Input
                    type="number"
                    id="vt1_power"
                    name="vt1_power"
                    value={formData.vt1_power}
                    onChange={handleInputChange}
                    placeholder="e.g., 145"
                    min="100"
                    max="1000"
                  />
                </FormGroup>
              )}
            </FormRow>

            <FormRow>
              <FormGroup style={{ maxWidth: '300px' }}>
                <Label htmlFor="vt2_bpm">VT2 HR (bpm)</Label>
                <Input
                  type="number"
                  id="vt2_bpm"
                  name="vt2_bpm"
                  value={formData.vt2_bpm}
                  onChange={handleInputChange}
                  placeholder="e.g., 165"
                  min="1"
                  max="220"
                />
              </FormGroup>

              {formData.activity_type === 'running' && (
                <FormGroup style={{ maxWidth: '300px' }}>
                  <Label htmlFor="vt1_bpm">VT2 Pace (mm:ss/km)</Label>
                  <Input
                    type="string"
                    id="vt2_pace"
                    name="vt2_pace"
                    value={formData.vt2_pace}
                    onChange={handleInputChange}
                    placeholder="e.g., 4:00"
                  />
                </FormGroup>
              )}

              {formData.activity_type === 'cycling' && (
                <FormGroup style={{ maxWidth: '300px' }}>
                  <Label htmlFor="vt1_bpm">VT2 Power (W)</Label>
                  <Input
                    type="number"
                    id="vt2_power"
                    name="vt2_power"
                    value={formData.vt2_power}
                    onChange={handleInputChange}
                    placeholder="e.g., 250"
                    min="100"
                    max="1000"
                  />
                </FormGroup>
              )}
            </FormRow>

            <FormRow>
              <FormGroup style={{ maxWidth: '300px' }}>
                <Label htmlFor="max_hr_bpm">Max HR (bpm)</Label>
                <Input
                  type="number"
                  id="max_hr_bpm"
                  name="max_hr_bpm"
                  value={formData.max_hr_bpm}
                  onChange={handleInputChange}
                  placeholder="e.g., 185"
                  min="1"
                  max="220"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="notes">Notes</Label>
              <TextArea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional notes about the test or data source..."
              />
            </FormGroup>

            <FormActions>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              )}
              <div style={{ flex: 1 }} />

              <Button type="button" variant="secondary" onClick={handleBack} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Thresholds & Zones'}
              </Button>
            </FormActions>
          </Form>

          <ZonePreviewContainer>
            <ZonePreview
              zoneModels={[hrZoneModel, paceZoneModel, powerZoneModel]}
              calculatedZones={calculatedZones}
              thresholdValues={thresholdValues}
              onZoneModelChange={handleZoneModelChange}
              editable={false}
            />
          </ZonePreviewContainer>
        </FormContainer>
      </Content>
    </Container>
  )
}
